import { logError } from "@jetblue/dotcom-fe-logger";

const REQUIRED_CONSENT_CATEGORY = 1 as const;
const FUNCTIONAL_CONSENT_CATEGORY = 2 as const;
const ADVERTISING_CONSENT_CATEGORY = 3 as const;

export type RawDecision = number[];
export type ConsentDecisionEmission = "changed" | "initial" | "unchanged";

type Decision = {
  advertising?: undefined | boolean;
  functional?: undefined | boolean;
  raw: RawDecision;
  required?: undefined | boolean;
};
type Subscriber = {
  callback: (decision: Decision) => void;
  label: string;
};

type State = {
  currentDecision: Decision | undefined;
  decisionKnown: ReturnType<typeof Promise.withResolvers>;
  subscribers: Subscriber[];
};
const state: State = {
  currentDecision: {
    advertising: undefined,
    functional: undefined,
    raw: [],
    required: undefined,
  },
  decisionKnown: Promise.withResolvers(),
  subscribers: [],
};

const notifySubscriber = (subscriber: Subscriber) => {
  try {
    if (state.currentDecision) {
      subscriber.callback(state.currentDecision);
    }
  } catch (error) {
    void logError({
      error,
      message: `The ${subscriber.label} consent subscriber threw while reacting to a decision`,
    });
  }
};

export function subscribeToUserConsentDecision(
  callback: Subscriber["callback"],
  label: Subscriber["label"],
) {
  const subscriber: Subscriber = { callback, label };
  notifySubscriber(subscriber);
  state.subscribers.push(subscriber);

  return () => {
    const index = state.subscribers.indexOf(subscriber);
    if (index !== -1) {
      state.subscribers.splice(index, 1);
    }
  };
}
export function getUserConsentDecision() {
  return state.currentDecision;
}
export function whenUserConsentDecisionKnown() {
  return state.decisionKnown.promise;
}
export function emit(newRawDecision: RawDecision): ConsentDecisionEmission {
  state.decisionKnown.resolve(undefined);

  const isPreviousDecisionWasKnown =
    state.currentDecision?.required !== undefined;

  const newDecision: Decision = {
    advertising: newRawDecision.includes(ADVERTISING_CONSENT_CATEGORY),
    functional: newRawDecision.includes(FUNCTIONAL_CONSENT_CATEGORY),
    raw: [...newRawDecision],
    required: newRawDecision.includes(REQUIRED_CONSENT_CATEGORY),
  };

  if (
    newDecision.advertising === state.currentDecision?.advertising &&
    newDecision.functional === state.currentDecision?.functional &&
    newDecision.required === state.currentDecision?.required
  ) {
    state.currentDecision = { ...state.currentDecision, raw: newDecision.raw };
    return "unchanged";
  }

  state.currentDecision = newDecision;

  // eslint-disable-next-line unicorn/no-useless-spread -- the copy is load-bearing, not cosmetic
  for (const subscriber of [...state.subscribers]) {
    if (state.subscribers.includes(subscriber)) {
      notifySubscriber(subscriber);
    }
  }

  return isPreviousDecisionWasKnown ? "changed" : "initial";
}
